import { Injectable } from '@angular/core';
import {RestApiService} from '../../../../shared/services/rest-api.service';
import {ProcessRequest} from '../../../models/process-request.model';
import {OfferModificationDialogComponent} from '../../../../shared/components/dialogs/offer-modification-dialog/offer-modification-dialog.component';
import {RequestMetricResult} from '../../../../shared/components/models/metrics/request-metric-result.enum';
import {ConfigurationService} from '../configuration/configuration.service';
import {RequestHeader} from '../../../../shared/models/request-offer/request-header.model';
import {MessageType} from '../../../../shared/models/request-offer/request-offer.enum';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {v4 as uuid} from 'uuid';
import {MatDialog} from '@angular/material';
import {MetricsService, RequestMetricName} from '../../../../shared/services/metrics.service';
import {LanguageService} from '../../../../shared/services/language.service';
import {ProcessService} from '../../process.service';
import {SelectedOfferService} from '../section-services/selected-offer/selected-offer.service';
import {PriceFrequency} from '../../../models/offer/offer-modification.model';
import {ApiRequestKey} from '../../../../shared/enums/api-request-key.enum';
import {ApiParamsService} from '../../api-params.service';

@Injectable({
  providedIn: 'root'
})
export class OfferModificationsApiService {

  lastSentModificationData: any;
  private readonly CLASSNAME = 'OfferModificationsService';
  private readonly API_REQUEST_KEY: ApiRequestKey = ApiRequestKey.OFFER_MODIFICATIONS;

  get offerModificationsEnabled(): boolean {
    return this.configurationService.uiConfig.offerModificationsEnabled;
  }

  constructor(private api: RestApiService,
              private dialog: MatDialog,
              private metricsService: MetricsService,
              private configurationService: ConfigurationService,
              private languageService: LanguageService,
              private processService: ProcessService,
              private selectedOfferService: SelectedOfferService,
              private apiParamsService: ApiParamsService) { }

  postOfferModification(partialData: {paymentFrequency: PriceFrequency, discountCode: string}, openWaitingDialog = true): Promise<any> {
    let uri = this.configurationService.uiConfig.offerModifyEndpoint;

    let body: ProcessRequest = {
      header: this.createRequestHeader(),
      body: this.createRequestBody(partialData)
    };

    if (openWaitingDialog) {
      this.dialog.open(OfferModificationDialogComponent, OfferModificationDialogComponent.getConfigBuilder().buildConfiguration({}));
    }

    this.sendRequestMetric(RequestMetricResult.SEND, body);

    return this.api.post(uri, body)
        .then(resp => { // RESPONSE
          this.sendRequestMetric(RequestMetricResult.SUCCESS);
        })
        .catch(error => { // RESPONSE
          this.sendRequestMetric(RequestMetricResult.ERROR, error);
          console.error(error);
        });
  }

  private createRequestHeader(): RequestHeader {
    const requestId = uuid();

    return {
      languageCode: this.languageService.getDefaultLanguage().toUpperCase(),
      requestId: requestId,
      responseId: requestId,
      messageType: MessageType.OFFER,
      requestDate: new Date(),
      responseDate: new Date(),
      processContext: {
        processId: this.processService.processId,
        businessParameters: this.apiParamsService.createBusinessParams({
          requestKey: this.API_REQUEST_KEY,
          payload: {
            offerId: this.selectedOfferService.selectedOffer.id
          }
        })
      },
      technicalParameters: this.apiParamsService.createTechnicalParams({
        requestKey: this.API_REQUEST_KEY
      })
    };
  }

  private createRequestBody(partialData: {paymentFrequency: PriceFrequency, discountCode: string}): any {
    const offerNumber = this.selectedOfferService.selectedOffer.counter.offerNumber;

    let offerParameters = this.selectedOfferService.selectedOffer.modifications
        .map(mod => ({
          code: mod.code,
          value: mod.options.find(option => option.selected) ? mod.options.find(option => option.selected).value : null
        }));

    offerParameters = offerParameters.filter(param => isNotNullOrUndefined(param.value));

    const body = {
      modificationData: {
        offerNumber: offerNumber,
        paymentFrequency: partialData.paymentFrequency,
        offerParameters: offerParameters,
        discountCode: partialData.discountCode
      }
    };

    this.lastSentModificationData = body.modificationData;
    return body;
  }

  private sendRequestMetric(type: RequestMetricResult, data?: any): void {
    const metricName = {
      className: this.CLASSNAME,
      methodName: 'postOfferModification'
    };

    this.metricsService.sendRequestMetric(<RequestMetricName>{...metricName, type}, data);
  }
}
